/**
 * Created by Pradeep Kumar on 5/17/2019.
 */

import {BetLine} from "@nolimitcity/slot-game/bin/core/betline/BetLine";
import {BetLineNumber} from "@nolimitcity/slot-game/bin/core/betline/BetLineNumber";
import {BetLinesView, IBetLinesViewConfig} from "@nolimitcity/slot-game/bin/core/betline/BetLinesView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {GameMode, IGameModeChangedData} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ISymbolStateConfig} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {ParsedInitData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedInitData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite} from "gsap";
import {TombOfNefertitiFreeSpinIntroView} from "../screen/freespinintro/TombOfNefertitiFreeSpinIntroView";
import {TombOfNefertitiGameConfig} from "../TombOfNefertitiGameConfig";
import {TombOfNefertitiSoundConfig} from "../TombOfNefertitiSoundConfig";
import {TombOfNefertitiAnimatedWinlineController} from "../winpresentation/animatedwinlines/TombOfNefertitiAnimatedWinlineController";

export class TombOfNefertitiBetLinesView extends BetLinesView {

    private readonly _numberFactoryMethodTON:(index:number) => BetLineNumber;
    private readonly _lineFactoryMethodTON:(index:number, numberPosition:number[], betLineData:number[]) => BetLine;

    private readonly _configTON:IBetLinesViewConfig;
    private _betLinesDataTON:number[][];
    private fsBetlineRevealSequence:number[] = [25, 40, 23, 39, 21, 30, 38, 29, 37, 28, 35, 27, 32, 36, 26, 34, 24, 33, 22, 31];

    private _betLineExplode:TimelineSprite[] = [];

    private _mode:string;

    private _additionalBetlinesText:PIXI.Text;
    private _treasureText:PIXI.Text;
    private _explode:TimelineSprite;

    /**
     * @constructs
     *
     * @param config
     * @param numberFactoryMethod
     * @param lineFactoryMethod
     */
    constructor(config:IBetLinesViewConfig, numberFactoryMethod:(index:number) => BetLineNumber, lineFactoryMethod:(index:number, numberPosition:number[], betLineData:number[]) => BetLine) {
        super(config, numberFactoryMethod, lineFactoryMethod);

        this._numberFactoryMethodTON = numberFactoryMethod;
        this._lineFactoryMethodTON = lineFactoryMethod;
        this._configTON = config;
    }

    /**
     * @description To add event listeners for this class, can not use super's method because the handler method is private in super.
     */
    protected addEventListeners():void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onInitDataParsedTON(event.params[0]));
        EventHandler.addEventListener(this, TombOfNefertitiFreeSpinIntroView.FREESPIN_INTRO_REVEAL_BETLINES, () => this.onEnteringFreeSpin());
        EventHandler.addEventListener(this, GameModeEvent.CHANGE_MODE, (event:GameEvent) => this.onChangeMode(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_RESTORE, () => this.onExitingFreeSpin(true));
    }

    /**
     * @description To construct betlines path and draw betline numbers.
     */
    protected draw():void {
        if(!this._resourcesLoaded || !this._dataInitialized) {
            return;
        }

        const map:number[] = this._configTON.map;

        // free spin betline numbers
        for(let i:number = 0; i < map.length; i += 2) {
            const betLineNumber:BetLineNumber = this._numberFactoryMethodTON(map[i]);
            const betLineNumberPosition:number[] = betLineNumber.setPosition(this._betLinesDataTON.length, i);
            betLineNumber.visible = false;
            this._betLineNumbers.push(betLineNumber);

            const betLine:BetLine = this._lineFactoryMethodTON(map[i], betLineNumberPosition, this._betLinesDataTON[map[i] - 1]);
            this._betLines.push(betLine);
        }

        // main game betline numbers
        for(let i:number = 1; i < map.length; i += 2) {
            const betLineNumber:BetLineNumber = this._numberFactoryMethodTON(map[i]);
            const betLineNumberPosition:number[] = betLineNumber.setPosition(this._betLinesDataTON.length, i);
            this._betLineNumbers.push(betLineNumber);

            const betLine:BetLine = this._lineFactoryMethodTON(map[i], betLineNumberPosition, this._betLinesDataTON[map[i] - 1]);
            this._betLines.push(betLine);
        }

        this._betLines.sort((a:BetLine, b:BetLine) => {
            return a.index - b.index;
        });

        this._betLineNumbers.sort((a:BetLineNumber, b:BetLineNumber) => {
            return a.index - b.index;
        });

        this.initializeBetlineIndicatorsExplosion();

        this.createAnimations();
    }

    protected initializeBetlineIndicatorsExplosion():void {
        let item:TimelineSprite,
            lineIndex:number;

        const posOffset:number = 2.5,
            layer:PIXI.Container = StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.REEL_BACKGROUND.name);

        for(let i:number = 0; i < this.fsBetlineRevealSequence.length; ++i) {
            item = new TimelineSprite(GameResources.getTextures("betLineExplode"));

            lineIndex = this.fsBetlineRevealSequence[i] - 1;

            item.anchor.set(0.5);
            item.x = this._betLineNumbers[lineIndex].x - posOffset;
            item.y = this._betLineNumbers[lineIndex].y - posOffset;

            item.visible = false;

            layer.addChild(item);
            this._betLineExplode.push(item);
        }
    }

    /**
     * @description To work on parsed init data.
     *
     * @param data
     */
    private onInitDataParsedTON(data:ParsedInitData):void {
        this._betLinesDataTON = data.betLines;
        if(this._betLinesDataTON.length != this._configTON.map.length) {
            debugger;
            throw new Error(`Error: There are only ${this._configTON.map.length} betLines in the config order while the server sends ${this._betLinesDataTON.length} lines.`);
        }

        this._dataInitialized = true;
        this.draw();
    }

    private onEnteringFreeSpin():void {
        let tl:TimelineLite = new TimelineLite(),
            scale:number;

        for(let i:number = 0; i < this.fsBetlineRevealSequence.length; ++i) {
            tl.add(() => {
                let lineIndex = this.fsBetlineRevealSequence[i] - 1;
                this._betLineNumbers[lineIndex].scale.set(0);
                this._betLineNumbers[lineIndex].visible = true;

                TweenLite.to(this._betLineNumbers[lineIndex].scale, 0.1, {
                    x : 1,
                    y : 1
                });

                scale = (i % 2 === 0) ? (0.8 + (i * 0.1)) : (0.8 + ((i - 1) * 0.1));

                this._betLineExplode[i].scale.set(scale);
                this._betLineExplode[i].getAnimationAutoShowHide(true, true);

                EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiAnimatedWinlineController.PLAY_INDIVIDUAL_WINLINE, this._betLineNumbers[lineIndex].index - 1, true, false, true, true));

                if(i % 2 === 0) {
                    SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.FS_BETLINE_INDICATOR_REVEAL + (i / 2 + 1));
                }

            }, (i % 2 === 0) ? (i * 0.07) : ((i - 1) * 0.07));
        }

        tl.add(() => {
            TweenLite.to(() => {}, 0.3, {});
        }, tl.totalDuration() + 0.2);

        let animDuration = this._explode.totalDuration;

        tl.add(() => {
            SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.FS_BETLINE_INDICATOR_REVEAL_END);

            this._additionalBetlinesText.visible = true;
            this._additionalBetlinesText.alpha = 0;
            TweenLite.to(this._additionalBetlinesText, animDuration / 4, {alpha : 1});

            this._explode.getAnimationAutoShowHide(true, true);
        }, tl.totalDuration() + 0.3 + 0.2);

        tl.add(() => {
            EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiAnimatedWinlineController.CLEAN_UP_ANIMATED_WINLINE));
            TweenLite.to(this._additionalBetlinesText, animDuration / 4, {alpha : 0});
        }, tl.totalDuration() + animDuration / 2 + 0.3 + 0.2);

        tl.add(() => {
            this._treasureText.visible = true;
            this._treasureText.alpha = 0;
            TweenLite.to(this._treasureText, animDuration / 4, {alpha : 1});

            this.changeStateOfSymbols(TombOfNefertitiGameConfig.instance.SYMBOL_STATES.noWin!);
        }, tl.totalDuration() + animDuration / 4);

        tl.add(() => {
            TweenLite.to(this._treasureText, animDuration / 4, {alpha : 0});
        }, tl.totalDuration() + animDuration / 2 + 0.3 + 0.2);

        tl.add(() => {
            EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiFreeSpinIntroView.FREESPIN_INTRO_TRANSITION_IN_COMPLETE));

            this._additionalBetlinesText.visible = false;
            this._treasureText.visible = false;
        }, tl.totalDuration() + animDuration / 4);

    }

    private changeStateOfSymbols(state:ISymbolStateConfig):void {
        for(let i:number = 0; i < 15; i++) {
            let symbol = Reels.getSymbol(i, 0);
            symbol!.changeState({
                state : state,
                fadeInDuration : 0
            });
        }
    }

    private onChangeMode(data:IGameModeChangedData):void {
        this._mode = data.newMode;

        if(this._mode.indexOf(GameMode.FREESPIN) === -1) {
            this.onExitingFreeSpin(false);
        }
    }

    private onExitingFreeSpin(isVisible:boolean):void {
        const fsBetlineStartIndex:number = 20;

        if(isVisible && this._mode.indexOf(GameMode.FREESPIN) === -1) {
            return;
        }

        for(let i:number = fsBetlineStartIndex; i < this._betLineNumbers.length; ++i) {
            this._betLineNumbers[i].visible = isVisible;
        }
    }

    private createAnimations():void {
        const textStyle:TextStyleOptions = {
                fill : ["#fbbd16", "#fff47b", "#fbbd16"],
                fillGradientStops : [0.1, 0.5, 0.9],
                fillGradientType : 0,
                fontSize : 80,
                fontFamily : TombOfNefertitiGameConfig.instance.USABLE_FONT,
                dropShadow : true,
                dropShadowAlpha : 1,
                dropShadowAngle : 90,
                dropShadowBlur : 6,
                dropShadowColor : "#250e08",
                dropShadowDistance : 5,
                padding : 8,
                stroke : "#4a1703",
                strokeThickness : 4
            },
            stageCenter:number[] = Helper.getSymbolPositions(Math.floor(TombOfNefertitiGameConfig.instance.REELS_NUM / 2), 0);

        this._additionalBetlinesText = new PIXI.Text("+20 " + Translation.translate("WIN LINES"), textStyle);
        this._additionalBetlinesText.visible = false;
        this._additionalBetlinesText.anchor.set(0.5);
        this._additionalBetlinesText.position.set(stageCenter[0], stageCenter[1]);

        this._treasureText = new PIXI.Text(Translation.translate("Get ready for the treasure"), textStyle);
        this._treasureText.visible = false;
        if((this._treasureText.width > TombOfNefertitiGameConfig.instance.REEL_AREA_WIDTH * 0.9)) {
            this.fitTreasureText();
        }

        this._treasureText.anchor.set(0.5);
        this._treasureText.position.set(stageCenter[0], stageCenter[1]);

        this._explode = new TimelineSprite(GameResources.getTextures('explode'));
        this._explode.visible = false;
        this._explode.scale.set(4);
        this._explode.anchor.set(0.5);
        this._explode.position.set(stageCenter[0], stageCenter[1]);

        StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.BET_LINES.name).addChild(this._explode);
        StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.BET_LINES.name).addChild(this._additionalBetlinesText);
        StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.BET_LINES.name).addChild(this._treasureText);
    }

    private fitTreasureText():void {
        let scaleValue:number = ((TombOfNefertitiGameConfig.instance.REEL_AREA_WIDTH * 0.9) / this._treasureText.width);
        this._treasureText.scale.set(scaleValue);
    }
}